import React, { useRef, useCallback, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReCAPTCHA } from 'react-google-recaptcha';
import { Box, Link, Center, HStack, Text } from '@chakra-ui/react';
import { SIGNUP_METHOD } from '../../../../../constants/userSetting';
import { RECAPTCHA_TOKEN_FIELD } from '../../../../../constants/recaptchable';
import authenticationService from '../../../../../service/authenticationService';
import { getInputFieldProps as _getInputFieldProps } from '../../../utils/propsBuilder';
import extractServerErrorMessages from '../../../utils/extractServerErrorMessages';
import { PANEL } from '../../../constants/panel';
import { useAppContext } from '../../../context/appContext';
import { useFlash } from '../../../hooks';
import { Button, Input, TitleText, ErrorBanner } from '../../../components';
import {
  FacebookAccount,
  LineAccount,
  OAuthApplicationLinks,
} from '../../components';

const RESET_PASSWORD_PATH = '/users/password/new';

const i18nKeyMap = {
  [SIGNUP_METHOD.EMAIL_AND_MOBILE]: {
    label: 'Login - email or mobile phone - label',
    placeholder: 'Login - email or mobile phone - placeholder',
  },
  [SIGNUP_METHOD.EMAIL]: {
    label: 'Login - email - label',
    placeholder: 'Login - email - placeholder',
  },
  [SIGNUP_METHOD.MOBILE]: {
    label: 'Login - mobile phone - label',
    placeholder: 'Login - mobile phone - placeholder',
  },
};

export default function LoginForm() {
  const { t } = useTranslation();
  const {
    setCurrentPanel,
    userSetting,
    defaultAccount,
    grecaptcha,
    isRecaptchaEnabled,
    recaptchaSiteKey,
    isFacebookLoginEnabled,
    isLineLoginEnabled,
    customerAuthenticationOauthApplications,
  } = useAppContext();
  const {
    errors,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useFormContext();
  const [errorMessages, setErrorMessages] = useState([]);
  const refReCAPTCHA = useRef();

  const i18nKey = i18nKeyMap[userSetting.signup_method];

  const { field: emailAndMobileField } = useController({
    name: 'emailAndMobile',
    control,
    defaultValue: defaultAccount?.email || defaultAccount?.mobile_phone || null,
  });
  const { field: passwordField } = useController({
    name: 'password',
    control,
    defaultValue: '',
  });

  const onSubmit = async (data) => {
    setErrorMessages([]);
    const { password, emailAndMobile } = data;

    try {
      const urlQueries = new URLSearchParams(window.location.search);
      const encryptData = urlQueries.get('encrypt_data');
      const payload = {
        user: {
          password,
          mobile_phone_or_email: emailAndMobile,
        },
        encrypt_data: encryptData,
      };
      if (isRecaptchaEnabled) {
        payload[
          RECAPTCHA_TOKEN_FIELD
        ] = await refReCAPTCHA.current?.executeAsync();
        refReCAPTCHA.current?.reset();
      }
      const result = await authenticationService.signIn(payload);
      const { redirect_to } = result.data;
      window.location.href = redirect_to;
    } catch (error) {
      const data = error?.response?.data ?? {};
      let input = data;
      if (data.unconfirmed_email && !data.reached_rate_limit) {
        input = {
          error_messages: [
            t('Login - Error - unconfirmed email', {
              email: data.unconfirmed_email,
            }),
          ],
        };
      }
      setErrorMessages(extractServerErrorMessages(input));
    }
  };

  const getInputFieldProps = useCallback(
    ({ field, error }) => _getInputFieldProps({ field, error, t }),
    [t],
  );

  useFlash((flash) => {
    if (typeof flash?.alert === 'string' && flash.alert) {
      setErrorMessages(
        extractServerErrorMessages({
          error_messages: [flash.alert],
        }),
      );
    }
  });

  return (
    <>
      <Box mb="72px">
        <TitleText mb="30px">{t('Sign In')}</TitleText>
        <ErrorBanner errorMessages={errorMessages} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mb="10px">
            <Input
              label={t(i18nKey.label)}
              placeholder={t(i18nKey.placeholder)}
              {...getInputFieldProps({
                field: emailAndMobileField,
                error: errors?.emailAndMobile,
              })}
            />
          </Box>
          <Box mb="10px">
            <Input.PasswordInput
              label={t('Password')}
              placeholder={t('Password (8 characters minimum)')}
              {...getInputFieldProps({
                field: passwordField,
                error: errors?.password,
              })}
            />
          </Box>
          <Box my="20px">
            <Link href={RESET_PASSWORD_PATH} fontSize="1.2rem" color="#3493FB">
              {t('Forgot Password')}
            </Link>
          </Box>
          <Button
            isFullWidth
            type="submit"
            onClick={handleSubmit(onSubmit)}
            isLoading={isSubmitting}
          >
            {t('Lets go')}
          </Button>
          <OAuthApplicationLinks
            applications={customerAuthenticationOauthApplications}
          />
          {(isFacebookLoginEnabled || isLineLoginEnabled) && (
            <Box mt="24px">
              <Text
                fontSize="12px"
                fontWeight={500}
                color="#272D36"
                textAlign="center"
                mb="8px !important"
              >
                {t('login in with social account')}
              </Text>
              <Center>
                <HStack spacing="12px">
                  {isFacebookLoginEnabled && (
                    <FacebookAccount currentPanel={PANEL.LOGIN} />
                  )}
                  {isLineLoginEnabled && <LineAccount />}
                </HStack>
              </Center>
            </Box>
          )}
          {isRecaptchaEnabled && (
            <ReCAPTCHA
              ref={refReCAPTCHA}
              size="invisible"
              grecaptcha={grecaptcha}
              sitekey={recaptchaSiteKey}
            />
          )}
        </form>
      </Box>
      {/* @TODO: add third party auth */}
      <TitleText mb="24px">{t('Not a member')}</TitleText>
      <Button
        isFullWidth
        onClick={() => setCurrentPanel(PANEL.SIGN_UP)}
        variant="outline"
      >
        {t('Sign Up')}
      </Button>
    </>
  );
}
